// Shared constants, utilities, data, and styles for carousel components

import {
  IMG_01_DEFAULT,
  IMG_01_HOVER,
  IMG_02_DEFAULT,
  IMG_02_HOVER,
  IMG_03_DEFAULT,
  IMG_03_HOVER,
  IMG_04_DEFAULT,
  IMG_04_HOVER,
  IMG_05_DEFAULT,
  IMG_05_HOVER,
  IMG_06_DEFAULT,
  IMG_06_HOVER,
  IMG_07_DEFAULT,
  IMG_07_HOVER,
} from "./carouselImageUrls";
import { cloudflareCDN } from "ui";

// ============================================================================
// Constants
// ============================================================================

// Animation config
export const ANIM_DURATION_MS = 600;
export const MOBILE_ANIM_DURATION_MS = 350; // Faster animation for mobile

// Card dimensions (MaterialCarousel)
export const MATERIAL_CARD_WIDTH_PX = 395;
export const MATERIAL_CARD_GAP_PX = 32;

// Card dimensions (RoundMaterialCarousel)
export const ROUND_CARD_WIDTH_PX = 273;
export const ROUND_CARD_GAP_PX = 22;

// Arc configuration (RoundMaterialCarousel)
export const ANGLE_STEP_DEG = 12;
export const ARC_RADIUS_MULTIPLIER = 2.0;
export const CAROUSEL_IMAGE_CDN_FORMAT = "width=1200,format=auto";
export const CAROUSEL_PREVIEW_CDN_FORMAT = "width=1800,format=auto";

// Feature flags
export const ENABLE_EXPANSION = false;

// Expansion dimensions
export const EXPANDED_WIDTH_MULTIPLIER = 2;
export const EXPANDED_HEIGHT_MULTIPLIER = 1.2;
export const BASE_CARD_WIDTH = 395;
export const BASE_CARD_HEIGHT = 610;

// Viewport breakpoints
export const MOBILE_BREAKPOINT = 600;
export const PHONE_VIEWPORT_BREAKPOINT = 899.95;

// Animation timing
export const FLIP_TRANSITION_DURATION = "600ms";

// ============================================================================
// Card Data
// ============================================================================

// Preview image URLs for expanded view
const PREVIEW_URLS = [
  "https://storage.googleapis.com/mattoboard-staging.appspot.com/design_stream/carousel_cards/05_BATHROOM_DS%20PREVIEW.png", // id: 1
  "https://storage.googleapis.com/mattoboard-staging.appspot.com/design_stream/carousel_cards/06_BEDROOM_DS%20PREVIEW.png", // id: 2
  "https://storage.googleapis.com/mattoboard-staging.appspot.com/design_stream/carousel_cards/07_LIVINGROOM_DS%20PREVIEW.png", // id: 3
  "https://storage.googleapis.com/mattoboard-staging.appspot.com/design_stream/carousel_cards/01_CAFE_DS%20PREVIEW.png", // id: 4
  "https://storage.googleapis.com/mattoboard-staging.appspot.com/design_stream/carousel_cards/02_KITCHEN_DS%20PREVIEW.png", // id: 5
  "https://storage.googleapis.com/mattoboard-staging.appspot.com/design_stream/carousel_cards/03_RETAIL_DS%20PREVIEW.png", // id: 6
  "https://storage.googleapis.com/mattoboard-staging.appspot.com/design_stream/carousel_cards/04_OFFICE_DS%20PREVIEW.png", // id: 7
] as const;

export const CAROUSEL_CARDS = [
  {
    id: 1,
    front: cloudflareCDN(IMG_01_DEFAULT, CAROUSEL_IMAGE_CDN_FORMAT),
    back: cloudflareCDN(IMG_01_HOVER, CAROUSEL_IMAGE_CDN_FORMAT),
    preview: cloudflareCDN(PREVIEW_URLS[0], CAROUSEL_PREVIEW_CDN_FORMAT),
    alt: "Bathroom",
  },
  {
    id: 2,
    front: cloudflareCDN(IMG_02_DEFAULT, CAROUSEL_IMAGE_CDN_FORMAT),
    back: cloudflareCDN(IMG_02_HOVER, CAROUSEL_IMAGE_CDN_FORMAT),
    preview: cloudflareCDN(PREVIEW_URLS[1], CAROUSEL_PREVIEW_CDN_FORMAT),
    alt: "Bedroom",
  },
  {
    id: 3,
    front: cloudflareCDN(IMG_03_DEFAULT, CAROUSEL_IMAGE_CDN_FORMAT),
    back: cloudflareCDN(IMG_03_HOVER, CAROUSEL_IMAGE_CDN_FORMAT),
    preview: cloudflareCDN(PREVIEW_URLS[2], CAROUSEL_PREVIEW_CDN_FORMAT),
    alt: "Living Room",
  },
  {
    id: 4,
    front: cloudflareCDN(IMG_04_DEFAULT, CAROUSEL_IMAGE_CDN_FORMAT),
    back: cloudflareCDN(IMG_04_HOVER, CAROUSEL_IMAGE_CDN_FORMAT),
    preview: cloudflareCDN(PREVIEW_URLS[3], CAROUSEL_PREVIEW_CDN_FORMAT),
    alt: "Cafe",
  },
  {
    id: 5,
    front: cloudflareCDN(IMG_05_DEFAULT, CAROUSEL_IMAGE_CDN_FORMAT),
    back: cloudflareCDN(IMG_05_HOVER, CAROUSEL_IMAGE_CDN_FORMAT),
    preview: cloudflareCDN(PREVIEW_URLS[4], CAROUSEL_PREVIEW_CDN_FORMAT),
    alt: "Kitchen",
  },
  {
    id: 6,
    front: cloudflareCDN(IMG_06_DEFAULT, CAROUSEL_IMAGE_CDN_FORMAT),
    back: cloudflareCDN(IMG_06_HOVER, CAROUSEL_IMAGE_CDN_FORMAT),
    preview: cloudflareCDN(PREVIEW_URLS[5], CAROUSEL_PREVIEW_CDN_FORMAT),
    alt: "Retail",
  },
  {
    id: 7,
    front: cloudflareCDN(IMG_07_DEFAULT, CAROUSEL_IMAGE_CDN_FORMAT),
    back: cloudflareCDN(IMG_07_HOVER, CAROUSEL_IMAGE_CDN_FORMAT),
    preview: cloudflareCDN(PREVIEW_URLS[6], CAROUSEL_PREVIEW_CDN_FORMAT),
    alt: "Office",
  },
] as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get card at any index using modulo for infinite scrolling
 */
export const getCardAtIndex = <T extends readonly { id: number }[]>(
  index: number,
  cards: T,
): T[number] => {
  const n = cards.length;
  const materialIndex = ((index % n) + n) % n; // Handle negative indices
  return cards[materialIndex];
};

/**
 * Get the centered (hero) card index based on scroll offset
 */
export const getCenteredIndex = (
  scrollOffset: number,
  cardWidth: number,
  cardGap: number,
): number => {
  const totalCardWidth = cardWidth + cardGap;
  return Math.round(scrollOffset / totalCardWidth);
};

// ============================================================================
// Style Objects
// ============================================================================

export const CARD_WRAPPER_STYLE = {
  position: "absolute" as const,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none" as const,
};

export const MOBILE_CARD_WRAPPER_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  padding: "20px",
  boxSizing: "border-box" as const,
};

export const EXPANDED_IMAGE_STYLE = {
  width: "100%",
  height: "100%",
  objectFit: "cover" as const,
  borderRadius: "16px",
};

export const CARD_INNER_STYLE = {
  position: "relative" as const,
  width: "100%",
  height: "100%",
};

export const CARD_FACE_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const CARD_BACK_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
};

export const CARD_IMAGE_STYLE = {
  width: "100%",
  height: "100%",
  objectFit: "cover" as const,
  borderRadius: "16px",
};
